import type { ReactNode } from "react";
import { Artefato, type EstadoArtefato } from "../../components/Artefato.js";
import { Botao, cx } from "../../components/ui.js";
import { DEF_ETAPAS, ETAPAS, type Etapa } from "./etapas.js";

/**
 * Estrutura de cada etapa do atendimento.
 *
 * Três faixas fixas: o que já foi montado (a moldura), o trabalho da etapa, e as
 * ações. O operador está de pé, com uma mão no celular e a outra livre; as ações
 * ficam sempre no mesmo lugar, no alcance do polegar, acima da área do gesto.
 */
export function PainelEtapa({
  etapa,
  etapas = ETAPAS,
  espectador,
  contexto,
  fotoUrl,
  capaUrl,
  compostoUrl,
  estadoArtefato,
  children,
  aoVoltar,
  acao,
}: {
  etapa: Etapa;
  /** As etapas do modo do atendimento, para a trilha: o "Só fotografia" tem menos. */
  etapas?: readonly Etapa[];
  espectador?: string;
  contexto?: string;
  fotoUrl?: string | null;
  capaUrl?: string | null;
  compostoUrl?: string | null;
  estadoArtefato?: EstadoArtefato;
  children: ReactNode;
  aoVoltar?: () => void;
  acao?: ReactNode;
}) {
  const def = DEF_ETAPAS[etapa];

  return (
    <div className="flex min-h-full flex-col">
      <Trilha etapa={etapa} etapas={etapas} />

      <div className="border-b border-borda px-4 py-3">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <Artefato
            fotoUrl={fotoUrl}
            capaUrl={capaUrl}
            compostoUrl={compostoUrl}
            estado={estadoArtefato}
            tamanho="tira"
            rotulos={false}
          />
          <div className="min-w-0 flex-1">
            {espectador ? (
              <p className="truncate font-display text-[1.25rem] leading-tight tracking-[-0.01em]">
                {espectador}
              </p>
            ) : (
              <p className="font-display text-[1.25rem] leading-tight text-texto-fraco">
                Novo atendimento
              </p>
            )}
            {contexto && (
              <p className="mt-0.5 truncate text-[0.8125rem] text-texto-suave">{contexto}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 py-5">
        <div className="mx-auto max-w-lg">
          <h2 className="mb-4 text-[0.75rem] font-semibold uppercase tracking-[0.13em] text-texto-fraco">
            {def.titulo}
            {def.opcional && <span className="ml-2 normal-case tracking-normal">— opcional</span>}
          </h2>
          {children}
        </div>
      </div>

      <div className="area-segura-inferior sticky bottom-0 border-t border-borda bg-fundo/95 px-4 pt-3 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          {aoVoltar && (
            <Botao tom="fantasma" onClick={aoVoltar}>
              Voltar
            </Botao>
          )}
          <div className="flex-1">{acao}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Marcas de posição no fluxo.
 *
 * Não são números: o operador pula etapas opcionais e volta atrás, então "passo
 * 3 de 7" seria falso. São traços — mostram onde ele está e quanto falta, sem
 * prometer uma sequência rígida.
 */
function Trilha({ etapa, etapas }: { etapa: Etapa; etapas: readonly Etapa[] }) {
  const atual = etapas.indexOf(etapa);

  return (
    <div className="flex gap-px" aria-hidden>
      {etapas.map((e, i) => (
        <span
          key={e}
          className={cx(
            "h-0.5 flex-1 transition-colors",
            i < atual ? "bg-brasa/60" : i === atual ? "bg-brasa" : "bg-superficie-alta",
          )}
        />
      ))}
    </div>
  );
}
